import React, { useMemo, useState } from 'react';
import { Box, Chip, Divider, Menu, Stack, Typography, useTheme } from '@mui/material';
import { PurchaseOrderItem, PurchaseOrderRecommendation } from '@domain/purchasing';
import { Icon, IconSize } from '@shared/icons';

const MoqType = {
    RECOMMENDED: 'recommended',
    MOQ: 'MOQ',
} as const;

export interface PurchaseOrderItemMoqMenuProps {
    item: PurchaseOrderItem;
    recommendation: PurchaseOrderRecommendation;
}

export const PurchaseOrderItemMoqMenu: React.FC<PurchaseOrderItemMoqMenuProps> = ({
    item,
    recommendation,
}) => {
    const theme = useTheme();

    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(menuAnchorEl);

    const formatPrice = (price: number, currency?: string) => {
        if (currency) {
            return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
        }
        return price.toLocaleString();
    };

    const moqItems = useMemo(() => {
        if (item.minimumOrderQuantityType === 'items') {
            return item.minimumOrderQuantity;
        }

        return Math.ceil((item.minimumOrderQuantity || 0) / (item.purchasePrice || 0));
    }, [item.minimumOrderQuantity, item.purchasePrice]);

    const moq: { type: (typeof MoqType)[keyof typeof MoqType]; value?: number } | undefined = useMemo(() => {
        if (!recommendation || (!recommendation?.moq && !recommendation?.recommendedQuantity)) {
            return;
        }

        if (!recommendation?.moq?.value) {
            return {
                type: MoqType.RECOMMENDED,
                value: recommendation.recommendedQuantity,
            };
        }

        if (!recommendation?.recommendedQuantity || recommendation?.recommendedQuantity === 0) {
            return {
                type: MoqType.RECOMMENDED,
                value: 0,
            };
        }

        if (recommendation?.recommendedQuantity > (moqItems || 0)) {
            return {
                type: MoqType.RECOMMENDED,
                value: recommendation.recommendedQuantity,
            };
        }

        return {
            type: MoqType.MOQ,
            value: moqItems,
        };
    }, [recommendation]);

    return (
        <>
            {moq !== undefined && (
                <Chip
                    onClick={(e) => {
                        e.stopPropagation();
                        setMenuAnchorEl((prev) => (prev ? null : e.currentTarget));
                    }}
                    color={moq?.value && moq.value > (item.quantity || 0) ? 'warning' : 'primary'}
                    label={moq?.type === MoqType.RECOMMENDED ? `Recommended: ${moq.value || 0}` : `MOQ: ${moq?.value || 0}`}
                />
            )}
            <Menu
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={() => setMenuAnchorEl(null)}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Stack direction="column" gap={2} p={2} minWidth={250}>
                    <Stack direction="row" alignItems="center" gap={2}>
                        <Typography variant="body2" color="text.primary" flex={1} noWrap>
                            Recommended
                        </Typography>
                        <Typography variant="body2" color="text.primary" noWrap>
                            {recommendation?.recommendedQuantity ? recommendation.recommendedQuantity : 0} items
                        </Typography>
                    </Stack>

                    <Divider />


                    {item.minimumOrderQuantity && (
                        <Stack direction="column" gap={2} flex={1}>
                            {item.minimumOrderQuantityType === 'currency' && (
                                <Stack direction="row" alignItems="center" gap={1} flex={1}>
                                    <Typography flex={1} variant="body2" color="text.secondary" noWrap>
                                        MOQ
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {formatPrice(item.minimumOrderQuantity, item.currency)}
                                    </Typography>
                                </Stack>
                            )}

                            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flex={1}>
                                <Typography flex={1} variant="body2" color="text.secondary" noWrap>
                                    Purchase price
                                </Typography>

                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {formatPrice(item.purchasePrice || 0, item.currency)}
                                </Typography>
                            </Stack>

                            <Divider />

                            <Stack direction="column" gap={0} flex={1}>
                                <Stack direction="row" gap={2} flex={1}>
                                    <Typography variant="body2" color="text.primary" flex={1} noWrap>
                                        MOQ
                                    </Typography>

                                    <Typography variant="body2" color="text.primary" noWrap>
                                        {moqItems ? `${moqItems} items` : '-'}
                                    </Typography>
                                </Stack>


                                {item.orderInMultiplesOf && (

                                    <Typography variant="caption" color="text.secondary" textAlign="right" noWrap>
                                        in multiples of {item.orderInMultiplesOf}
                                    </Typography>

                                )}
                            </Stack>



                        </Stack>
                    )}


                </Stack>
            </Menu>
        </>
    );
};
